import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm } from './client'

/**
 * Settings -> Agent Releases, calea noua. Perechile vechi, din
 * `application/controllers/settings/Agent_releases.php`:
 *
 *   index        -> GET  /settings/agent_releases
 *   upload       -> POST /settings/agent_releases/upload        (multipart)
 *   revert       -> POST /settings/agent_releases/revert
 *   force_update -> POST /settings/agent_releases/force_update
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`/`apiPostForm`.
 *
 * TOT CE E DE SOCOTIT S-A SOCOTIT PE SERVER. Starea fiecarei masini, ce e pe
 * disc, cifrele graficelor, si chiar despachetarea randurilor de jurnal
 * ("Agent updated to X from Y (auto)") vin gata facute: in portal stateau in
 * macheta, iar aici ar fi insemnat doua expresii regulate si o duzina de
 * praguri scrise a doua oara, in alt limbaj.
 */

/** Un fisier publicat, pe slot: cat ocupa, si scris pentru om. */
export type SlotFile = {
  size: number
  mb: string
}

export type AgentManifest = {
  version: string
  /** "27 Aug, 12:47" - fara an, ca in portal. */
  updated: string
  files: Record<string, SlotFile>
}

/** Un rand din flota, gata de desenat. */
export type FleetHost = {
  id: number
  name: string
  company: string
  os: string
  osIcon: string
  osName: string
  version: string
  auto: boolean
  status: 'current' | 'behind' | 'pending' | 'unknown'
  statusLabel: string
  statusBadge: string
  statusSort: number
  stuck: boolean
  silent: boolean
  agentCpu: number | null
  agentMb: number | null
  agentServiceCpu: number | null
  agentServiceMb: number | null
  agentAt: string
  ramTotal: number | null
  installed: string
  installedSort: number
  updated: string
  updatedSort: number
  updatedAtInstall: boolean
  lastSeen: string
  lastSeenExact: string
  lastSeenSort: number
  needsUpdate: boolean
}

export type FleetCounts = {
  published: boolean
  total: number
  current: number
  behind: number
  pending: number
  unknown: number
  late: number
  stuck: number
  silent: number
}

/** `UnixFleet::headPulse()`: cele patru grafice din dreapta. */
export type FleetPulse = {
  total: number
  fleet: { current: number; late: number; stuck: number }
  talk: { ok: number; rest: number }
  load: { extremely_low: number; very_low: number; medium: number; high: number; none: number }
  memory: { extremely_low: number; very_low: number; low: number; medium: number; high: number; none: number }
}

export type UpdateLogRow = {
  id: number
  when: string
  deviceId: number
  host: string
  company: string
  from: string
  to: string
  message: string
  kind: 'manual' | 'auto' | 'force'
  search: string
}

export type PublishedVersion = {
  version: string
  updated: string
  current: boolean
  files: number
}

export type PackageVersions = {
  pkg: string
  label: string
  versions: PublishedVersion[]
  error: string
}

export type AgentReleasesPage = {
  manifest: AgentManifest
  slots: Record<string, string>
  osBuilds: Record<string, [string, string][]>
  fields: Record<string, [string, string, string]>
  latest: string
  devices: FleetHost[]
  counts: FleetCounts
  pulse: FleetPulse
  history: UpdateLogRow[]
  versions: PackageVersions[]
  canUpdate: boolean
  /**
   * Regulile updater-ului on-prem (`shared/agent_rollout.json`), numai citire.
   * `null` / lipsa = instalarea nu e tinuta de updater: ecranul de azi.
   */
  updater?: UpdaterRollout | null
  /** `OD_UPDATER_SHARED` pus: Publish si Revert sunt oprite (si pe server). */
  updaterManaged?: boolean
}

/** O regula care atinge compania, in ordinea in care o incearca API-ul. */
export type UpdaterStep = {
  version: string
  /** Procentul se judeca pe aparat (determinist), nu pe companie. */
  percent: number
  kind: 'company' | 'sp' | 'infrastructure'
}

export type UpdaterCompany = {
  companyId: number
  company: string
  spId: number
  sp: string
  os: Record<string, UpdaterStep[]>
}

export type UpdaterRollout = {
  error: string
  os: string[]
  companies: UpdaterCompany[]
}

export const agentReleasesQueryKey = ['settings', 'agent_releases'] as const

export function agentReleasesQuery() {
  return queryOptions({
    queryKey: agentReleasesQueryKey,
    queryFn: () => apiGet<AgentReleasesPage>('/settings/agent_releases'),
  })
}

/**
 * Raspunsul unui capat care schimba ceva. `needCode` inseamna ca serverul a
 * trimis un cod pe email si asteapta sa fie scris - vine cu 400, deci ajunge
 * aici prin `ApiError`, si de-aia se citeste din corpul lui.
 */
export type WriteOutcome = {
  status?: string
  message?: string
  needCode?: boolean
  sentTo?: string
  version?: string
  pkg?: string
  updated?: number
}

/**
 * Publicarea: `multipart/form-data`, cu binarele alese si numarul de versiune.
 * `code` si `agree` se adauga la a doua incercare, dupa ce serverul a cerut
 * codul - ACEEASI `FormData`, cu fisierele deja citite.
 */
export function publishAgent(body: FormData) {
  return apiPostForm<WriteOutcome>('/settings/agent_releases/upload', body)
}

export function revertAgent(pkg: string, version: string, code?: string) {
  const fields: Record<string, string> = { pkg, version }

  if (code !== undefined) {
    fields.code = code
    fields.agree = 'I agree'
  }

  return apiPost<WriteOutcome>('/settings/agent_releases/revert', fields)
}

export function forceUpdateHosts(devices: number[]) {
  return apiPost<WriteOutcome>('/settings/agent_releases/force_update', { devices })
}
